/**
 * Serializes Data Reception file-activity loads for ONE scheduler instance (one mounted page).
 *
 * Scope of the guarantee: this limits the number of outstanding HTTP requests per scheduler. It does NOT
 * limit server-side SQL: cancelling or timing out an HTTP request does not cancel the query already running
 * on the server, and a remount (or another tab) creates a new scheduler with its own request. There is no
 * cross-instance cache or dedupe.
 *  - A request for the query that is already in flight is coalesced into it (repeated Refresh / duplicate effects).
 *  - A request for a different query is held as the single latest pending query (earlier pending ones are
 *    replaced) and starts as soon as the in-flight request settles (answer, error or client timeout). The
 *    in-flight outcome is then discarded, because it answers a query the user has already moved past.
 *  - The next poll is scheduled `pollMs` after a load SETTLES (success or failure), never on a fixed interval,
 *    so a slow response cannot pile up further requests.
 *  - dispose() (unmount / logout) stops polling and drops any in-flight or pending result.
 */
export interface ReceptionQuery {
  rtuId?: string;
  from?: string;
  to?: string;
  page: number;
  size: number;
  search?: string;
}

export const RECEPTION_POLL_MS = 30000;

export const receptionQueryKey = (query: ReceptionQuery): string =>
  JSON.stringify([
    query.rtuId ?? '',
    query.from ?? '',
    query.to ?? '',
    query.page,
    query.size,
    query.search ?? '',
  ]);

export interface ReceptionLoadHandlers<T> {
  fetch: (query: ReceptionQuery) => Promise<T>;
  /** Called when a request is sent. `background` is true for polls and manual refreshes of loaded content. */
  onStart: (query: ReceptionQuery, key: string, background: boolean) => void;
  /** Only called for a result that answers the latest requested query. */
  onSuccess: (result: T, query: ReceptionQuery, key: string) => void;
  /** Only called for an error that belongs to the latest requested query. */
  onError: (error: unknown, query: ReceptionQuery, key: string) => void;
  /** Called when nothing is in flight or pending any more. */
  onIdle: () => void;
}

interface Pending {
  query: ReceptionQuery;
  key: string;
}

export class ReceptionLoadScheduler<T> {
  private inFlightKey: string | null = null;
  private pending: Pending | null = null;
  private latest: Pending | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private hasSettledOnce = false;

  constructor(
    private readonly handlers: ReceptionLoadHandlers<T>,
    private readonly pollMs: number = RECEPTION_POLL_MS
  ) {}

  /** Request a load of `query`. Safe to call repeatedly; see class comment for coalescing rules. */
  request(query: ReceptionQuery): void {
    if (this.disposed) {
      return;
    }
    const key = receptionQueryKey(query);
    this.latest = { query, key };
    this.clearPoll();

    if (this.inFlightKey === null) {
      this.start(query, key);
      return;
    }
    if (this.inFlightKey === key) {
      // Same query already pending: coalesce, and drop any queued query the user has since abandoned.
      this.pending = null;
      return;
    }
    this.pending = { query, key };
  }

  dispose(): void {
    this.disposed = true;
    this.pending = null;
    this.latest = null;
    this.clearPoll();
  }

  private start(query: ReceptionQuery, key: string): void {
    this.inFlightKey = key;
    this.handlers.onStart(query, key, this.hasSettledOnce);
    let promise: Promise<T>;
    try {
      promise = Promise.resolve(this.handlers.fetch(query));
    } catch (error) {
      promise = Promise.reject(error);
    }
    promise.then(
      (result) => this.settle(key, query, { ok: true, result }),
      (error) => this.settle(key, query, { ok: false, error })
    );
  }

  private settle(
    key: string,
    query: ReceptionQuery,
    outcome: { ok: true; result: T } | { ok: false; error: unknown }
  ): void {
    this.inFlightKey = null;
    if (this.disposed) {
      return;
    }
    this.hasSettledOnce = true;

    if (this.pending && this.pending.key !== key) {
      // The user moved on while this ran: discard this answer and run the latest query next.
      const next = this.pending;
      this.pending = null;
      this.start(next.query, next.key);
      return;
    }
    this.pending = null;

    if (outcome.ok) {
      this.handlers.onSuccess(outcome.result, query, key);
    } else {
      this.handlers.onError(outcome.error, query, key);
    }
    this.handlers.onIdle();
    this.schedulePoll();
  }

  private schedulePoll(): void {
    this.clearPoll();
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      if (!this.disposed && this.latest) {
        this.request(this.latest.query);
      }
    }, this.pollMs);
  }

  private clearPoll(): void {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
